import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { AuditSession } from "../../../../models/audit/auditSession";
import { WorkpaperDataSlim } from "../../../../models/audit/workpaperData";
import { AlertTriangle, RefreshCw, AlertCircle, Sparkles } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "../../../../components/ui/context-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { toast } from "sonner";
import { ApiError, fetchFastApi } from "../../../../api/fastapi";
import { useAudit } from "../../../../contexts/auditContext";
import { useProcedureSessionTableShell } from "../../../../hooks/audit/useProcedureSessionTableShell";
import {
  useSessionRunsContext,
  createSessionRun,
  getHighlightCellClassName,
  HighlightEdges,
} from "../../../../contexts/sessionRunsContext";
import { CellErrorTooltipContent } from "../../../../components/audit/CellErrorTooltipContent";
import { CurrentSelection } from "./WorkPaperTableV2";
import { CellLockButton } from "./CellLockButton";
import { RegenerateWithInstructionsDialog } from "./RegenerateWithInstructionsDialog";
import remarkGfm from "remark-gfm";
import Markdown from "react-markdown";

const DEFAULT_COLUMN_WIDTH = 200;

/**
 * Phase 3: `DataCell` only reads `.id` off the source row, so it accepts
 * the light dry shape (`tableShell.sourceRows[*]`) — no heavy
 * `checkResults`/`attributeTestResults` needed at the support-column
 * cell level.
 */
type SourceRowLike = { id: string };

interface DataCellProps {
  sourceRow: SourceRowLike;
  column: AuditSession["procedure"]["columns"][number];
  columnData?: WorkpaperDataSlim["data"][string][number];
  currentSelection?: CurrentSelection;
  onCellSelection: ({
    checkId,
    sourceRowId,
    documentId,
    attributeId,
    checkResultId,
    supportColumnId,
    value,
    evalColumnId,
  }: {
    checkId?: string;
    sourceRowId?: string;
    documentId?: string;
    attributeId?: string;
    checkResultId?: string;
    supportColumnId?: string;
    value?: string;
    evalColumnId?: string;
  }) => void;
  onCellEdit?: (
    newValue: string,
    sourceRowId: string,
    supportColumnId: string,
  ) => Promise<void>;
  width?: number;
  isLast?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  isTimeout?: boolean;
  errorMessage?: string;
  isHighlighted?: boolean;
  // Per-side border sides for the highlighted-region merge — omitted (or all
  // undefined-neighbor) call sites fall back to a full standalone border via
  // `getHighlightCellClassName`'s default.
  highlightEdges?: HighlightEdges;
  auditSessionId: string;
  // MS-2533: grid lock toggle — locking stamps the row to the current column
  // version (re-affirmed against the current definition).
  onToggleLock?: (
    sourceRowId: string,
    supportColumnId: string,
    currentLockedBy: string | null,
    currentColumnVersion?: number,
  ) => void;
  // Notifies the parent that a rerun-with-instructions just started for this
  // cell, so it can keep the "Running" badge lit through the persist-to-control
  // phase (where no server run exists yet). Parent owns the placeholder run;
  // the returned cleanup removes it early if the request fails.
  onRerunPending?: (
    sourceRowId: string,
    targetColumnIds: string[] | null,
  ) => () => void;
}

export const DataCell = ({
  sourceRow,
  column,
  columnData,
  onCellSelection,
  onCellEdit,
  currentSelection,
  width,
  isLast,
  isLoading,
  hasError,
  isTimeout = false,
  errorMessage,
  isHighlighted = false,
  highlightEdges,
  auditSessionId,
  onToggleLock,
  onRerunPending,
}: DataCellProps) => {
  // Phase 3: envelope-only reads from `controlSession.status`. The heavy
  // `useProcedureSession` call this component used to host is GONE —
  // procedure-session documents come from the cheap table-shell cache
  // (dedupe-friendly), so we no longer fan out the per-procedure megaquery
  // from every support-column cell.
  const { controlSession, updateCurrentDocument } = useAudit();
  const { addSessionRun, isHighlightFading } = useSessionRunsContext();
  const { data: tableShell } = useProcedureSessionTableShell(auditSessionId);
  const cellRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(columnData?.value || "");
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const widthStyle = useMemo<React.CSSProperties>(() => {
    const w = width ?? DEFAULT_COLUMN_WIDTH;
    return { minWidth: w, flex: `1 0 ${w}px`, boxSizing: "border-box" };
  }, [width]);

  const getDocumentInControlSession = useCallback(
    (documentId: string) => {
      if (!tableShell) return null;
      // Phase 3 schema invariant: a document referenced from a cell on this
      // procedure session is always present on this procedure session's
      // documents list (Phase 1 BE-4 / Phase 2 documents slice). No
      // cross-procedure walk needed.
      return (
        tableShell.documents.find((doc) => doc.documentId === documentId) ??
        null
      );
    },
    [tableShell],
  );

  const isSelected = useMemo(() => {
    return (
      currentSelection?.supportColumnId === column.supportColumn?.id &&
      currentSelection?.sourceRowId === sourceRow.id
    );
  }, [currentSelection, sourceRow.id, column.supportColumn?.id]);

  const isStale = useMemo(() => {
    // A locked cell is deliberately pinned — the stale warning's call to
    // action (rerun) is exactly what the lock blocks, so the padlock
    // supersedes the ⚠.
    if (!columnData || isLoading || columnData.lockedBy) return false;
    return columnData.columnVersion !== column.columnVersion;
  }, [columnData, column.columnVersion, isLoading]);

  const currentDocument = getDocumentInControlSession(
    columnData?.sourceDocument?.id || "",
  );

  // Update editValue when columnData changes (for optimistic updates)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(columnData?.value || "");
    }
  }, [columnData?.value, isEditing]);

  const handleSave = async () => {
    const originalValue = columnData?.value || "";

    // Only save if the value has actually changed
    if (editValue !== originalValue && onCellEdit && column.supportColumn?.id) {
      try {
        await onCellEdit(editValue, sourceRow.id, column.supportColumn.id);
      } catch (error) {
        // Error is handled in the parent function
        console.error("Failed to save cell edit:", error);
      }
    }

    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(columnData?.value || "");
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleClick = () => {
    // If already selected, enter edit mode
    if (isLoading) {
      return;
    }
    if (isSelected && !isEditing && controlSession?.status !== "complete") {
      setIsEditing(true);
      setEditValue(columnData?.value || "");
      // Focus the input after state update
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Place cursor at the end instead of selecting all text
          inputRef.current.setSelectionRange(
            inputRef.current.value.length,
            inputRef.current.value.length,
          );
        }
      }, 0);
      return;
    }

    // Select the cell and immediately enter edit mode
    const documentData = {
      id: currentDocument?.id,
      documentId: currentDocument?.documentId,
      type: columnData?.sourceDocument?.id,
      fileType: currentDocument?.fetchableDocument?.fileType,
      fileName: currentDocument?.fetchableDocument?.fileName,
      procedureSessionId: auditSessionId,
    };
    onCellSelection({
      documentId: currentDocument?.documentId,
      sourceRowId: sourceRow.id,
      supportColumnId: column.supportColumn?.id,
      checkId: undefined,
      attributeId: undefined,
      checkResultId: undefined,
      value: columnData?.value,
    });
    updateCurrentDocument(documentData);

    // Immediately enter edit mode after selection
    setIsEditing(true);
    setEditValue(columnData?.value || "");
    // Focus the input after state update
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // Place cursor at the end instead of selecting all text
        inputRef.current.setSelectionRange(
          inputRef.current.value.length,
          inputRef.current.value.length,
        );
      }
    }, 0);

    // Delay scroll to allow right panel to open and layout to adjust
    setTimeout(() => {
      if (cellRef.current) {
        const rect = cellRef.current.getBoundingClientRect();
        const scrollContainer = cellRef.current.closest(".overflow-x-scroll");

        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          // Check if cell is visible within the scroll container
          const isVisibleInContainer =
            rect.left >= containerRect.left &&
            rect.right <= containerRect.right;

          // Only scroll if the cell is not visible in the container
          if (!isVisibleInContainer) {
            cellRef.current.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "start",
            });
          }
        }
      }
    }, 300);
  };

  const handleRerunCell = async () => {
    if (!column.supportColumn?.id) {
      toast.error("Missing information to re-run cell");
      return;
    }
    if (columnData?.lockedBy) {
      toast.info("Cell is locked");
      return;
    }

    try {
      // Create and add session run optimistically
      const sessionRun = createSessionRun(auditSessionId, sourceRow.id, [
        column.id,
      ]);
      addSessionRun(sessionRun);

      toast.info("Regenerating cell...");
      await fetchFastApi({
        path: `/matching/match-session/${auditSessionId}/work-paper-data/generate/v2/sample/${sourceRow.id}`,
        method: "POST",
        body: { target_column_ids: [column.id] },
      });
    } catch (error) {
      console.error("Error re-running cell:", error);
      toast.error("Error re-running cell");
    } finally {
      // no-op
    }
  };

  const handleRerunCellWithInstructions = async (
    instructions: string,
    persistToControl: boolean,
  ) => {
    if (!column.supportColumn?.id) {
      toast.error("Missing information to re-run cell");
      return;
    }
    if (columnData?.lockedBy) {
      toast.info("Cell is locked");
      return;
    }

    let clearPending: (() => void) | undefined;
    try {
      // Keep the cell "Running" continuously through the persist-to-control
      // phase; the parent owns the placeholder run that bridges the window
      // before the real rerun dispatches.
      clearPending = onRerunPending?.(sourceRow.id, [column.id]);

      toast.info("Regenerating cell with instructions...");
      await fetchFastApi({
        path: `/matching/match-session/${auditSessionId}/regenerate-with-instructions/sample/${sourceRow.id}/column/${column.id}`,
        method: "POST",
        body: {
          custom_instructions: instructions,
          // Omit the flag when unchecked: a backend without MS-2514 rejects
          // unknown fields (extra_forbidden), so plain reruns must not carry it.
          ...(persistToControl ? { persist_to_control: true } : {}),
        },
      });
    } catch (error) {
      console.error("Error re-running cell with instructions:", error);
      toast.error(
        error instanceof ApiError
          ? error.getUserMessage()
          : "Error re-running cell with instructions",
      );
      clearPending?.();
    }
  };

  // MS-2533: const capture so the lock toggle's closure keeps the narrowed
  // types. Only cells with data can be locked (the endpoint 404s otherwise).
  const lockToggle =
    onToggleLock && columnData && column.supportColumn
      ? {
          toggle: onToggleLock,
          supportColumnId: column.supportColumn.id,
          lockedBy: columnData.lockedBy,
        }
      : null;

  return (
    <ContextMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <ContextMenuTrigger asChild>
            <div
              ref={cellRef}
              data-source-row-id={sourceRow.id}
              data-column-id={column.id}
              data-selected={isSelected ? "true" : undefined}
              className={`relative group/cell text-sm px-3 py-2 border-l border-b border-stone-300 cursor-pointer hover:bg-stone-100 overflow-hidden transition-all ${
                isLast ? "border-r" : ""
              } ${isSelected ? "border border-stone-500 bg-stone-50" : ""} ${
                isLoading ? "bg-blue-50 animate-pulse" : ""
              } ${isStale || hasError ? "opacity-60" : ""} ${getHighlightCellClassName(
                isHighlighted,
                isHighlightFading,
                highlightEdges,
              )}`}
              role="button"
              onClick={handleClick}
              title={isSelected ? "Click to edit" : ""}
              style={widthStyle}
            >
              {isEditing && isSelected ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={handleSave}
                  onKeyDown={handleKeyDown}
                  className="w-full bg-transparent border-none outline-none p-0 m-0 text-sm break-words"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div
                  className={`flex items-center gap-1.5 min-w-0 ${isLoading ? "gradient-sweep" : ""}`}
                >
                  {hasError && (
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-600" />
                  )}
                  {isStale && !hasError && (
                    <AlertTriangle className="h-3.5 w-3.5 text-gray-500 shrink-0" />
                  )}
                  <div className="break-words min-w-0 overflow-wrap-anywhere cell-markdown">
                    {isLoading ? (
                      "Running..."
                    ) : columnData?.value ? (
                      <Markdown remarkPlugins={[remarkGfm]}>
                        {columnData.value}
                      </Markdown>
                    ) : null}
                  </div>
                </div>
              )}
              {lockToggle && (
                <CellLockButton
                  lockedBy={lockToggle.lockedBy}
                  disabled={controlSession?.status === "complete"}
                  onToggle={() =>
                    lockToggle.toggle(
                      sourceRow.id,
                      lockToggle.supportColumnId,
                      lockToggle.lockedBy,
                      column.columnVersion,
                    )
                  }
                />
              )}
            </div>
          </ContextMenuTrigger>
        </TooltipTrigger>
        {hasError && (
          <CellErrorTooltipContent
            isTimeout={isTimeout}
            errorMessage={errorMessage}
          />
        )}
        {isStale && !hasError && (
          <TooltipContent className="max-w-xs break-words">
            <p>This result is stale</p>
          </TooltipContent>
        )}
      </Tooltip>
      <ContextMenuContent className="w-52">
        <ContextMenuLabel>Cell Actions</ContextMenuLabel>
        <ContextMenuSeparator />

        <ContextMenuItem
          className="cursor-pointer flex items-center gap-2 justify-between"
          onClick={handleRerunCell}
          disabled={
            isLoading ||
            !!columnData?.lockedBy ||
            controlSession?.status === "complete"
          }
        >
          Re-run cell
          <RefreshCw className="h-4 w-4" />
        </ContextMenuItem>
        <ContextMenuItem
          className="cursor-pointer flex items-center gap-2 justify-between"
          onClick={() => setInstructionsDialogOpen(true)}
          disabled={
            isLoading ||
            !!columnData?.lockedBy ||
            controlSession?.status === "complete"
          }
        >
          Re-run with instructions
          <Sparkles className="h-4 w-4" />
        </ContextMenuItem>
      </ContextMenuContent>

      <RegenerateWithInstructionsDialog
        open={instructionsDialogOpen}
        onOpenChange={setInstructionsDialogOpen}
        onSubmit={handleRerunCellWithInstructions}
        title="Regenerate Cell with Instructions"
        description="Provide custom instructions to guide the regeneration of this cell."
      />
    </ContextMenu>
  );
};
